/**
 * Airport Dashboard — v3 Corporate Consulting
 * 
 * As-is vs To-be capacity comparison dashboard.
 * Clean editorial layout with Satoshi/DM Sans typography.
 */

import React, { useState, useEffect } from 'react';
import {
    BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Line, ComposedChart,
} from 'recharts';
import {
    LayoutDashboard, TrendingUp, TrendingDown, Minus, Clock,
    PlaneTakeoff, Users, Building2, Gauge, ArrowRight, Percent,
    ToggleLeft, ToggleRight, ChevronRight, BarChart3, Info,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { dashboardService } from '../services/dashboardService';
import type {
    DemandHorizon,
    DashboardComparison,
    CapacityKPIs,
    ProjectSummary,
} from '../services/dashboardService';
import type { Simulation } from '../services/simulations';

const TEXTS = {
    es: {
        eyebrow: 'Escenarios',
        title: 'Escenarios de Capacidad',
        subtitle: 'Comparación As-Is vs To-Be',
        previewNote: 'Vista previa. El lado As-Is usa los datos reales del análisis DACAMS. El To-Be requiere una simulación FTS y hoy usa una mejora provisional del AMS; la comparación con datos medidos (y por horizontes de demanda) llega con el puente FTS→to-be.',
        projectLabel: 'Proyecto (As-Is)',
        selectProject: 'Seleccionar proyecto...',
        simLabel: 'Simulación FTS (To-Be)',
        scenarioLabel: 'Escenario',
        demoFallback: 'Demo / Fallback',
        loading: 'Cargando datos de análisis...',
        capacityKpis: 'KPIs de Capacidad',
        horizon: 'Horizonte',
        improved: '(mejorado)',
        hourlyChart: 'Capacidad vs Demanda por Hora',
        movements: 'Movimientos',
        summary: 'Resumen del Análisis',
        airport: 'Aeropuerto',
        emptyTitle: 'Escenarios de Capacidad',
        emptyDesc: 'No hay proyectos. Crea uno desde el HUB DACAMS.',
        features: 'Características To-Be:',
        weekLabel: '+1 Semana',
        monthLabel: '+1 Mes',
        seasonLabel: 'Temporada Completa',
    },
    en: {
        eyebrow: 'Scenarios',
        title: 'Capacity Scenarios',
        subtitle: 'As-Is vs To-Be comparison',
        previewNote: 'Preview. The As-Is side uses the real DACAMS analysis data. The To-Be needs an FTS simulation and today relies on a provisional AMS improvement; the measured comparison (and the per-demand-horizon view) arrives with the FTS->to-be bridge.',
        projectLabel: 'Project (As-Is)',
        selectProject: 'Select project...',
        simLabel: 'FTS Simulation (To-Be)',
        scenarioLabel: 'Scenario',
        demoFallback: 'Demo / Fallback',
        loading: 'Loading analysis data...',
        capacityKpis: 'Capacity KPIs',
        horizon: 'Horizon',
        improved: '(improved)',
        hourlyChart: 'Hourly Capacity vs Demand',
        movements: 'Movements',
        summary: 'Analysis Summary',
        airport: 'Airport',
        emptyTitle: 'Capacity Scenarios',
        emptyDesc: 'No projects. Create one from the DACAMS HUB.',
        features: 'To-Be features:',
        weekLabel: '+1 Week',
        monthLabel: '+1 Month',
        seasonLabel: 'Full Season',
    },
};

const HORIZONS_I18N = {
    es: [
        { value: 'H+24' as DemandHorizon, label: 'H+24h' },
        { value: 'H+48' as DemandHorizon, label: 'H+48h' },
        { value: 'H+72' as DemandHorizon, label: 'H+72h' },
        { value: '1W' as DemandHorizon, label: '+1 Semana' },
        { value: '1M' as DemandHorizon, label: '+1 Mes' },
        { value: 'SEASON' as DemandHorizon, label: 'Temporada' },
    ],
    en: [
        { value: 'H+24' as DemandHorizon, label: 'H+24h' },
        { value: 'H+48' as DemandHorizon, label: 'H+48h' },
        { value: 'H+72' as DemandHorizon, label: 'H+72h' },
        { value: '1W' as DemandHorizon, label: '+1 Week' },
        { value: '1M' as DemandHorizon, label: '+1 Month' },
        { value: 'SEASON' as DemandHorizon, label: 'Full Season' },
    ],
};

const KPI_CONFIG: { key: keyof CapacityKPIs; label: string; unit: string; icon: React.ElementType; inverse?: boolean }[] = [
    { key: 'rac', label: 'RAC', unit: 'pos/h', icon: Gauge },
    { key: 'cc', label: 'CC', unit: 'mov/h', icon: Clock },
    { key: 'bci', label: 'BCI', unit: 'idx', icon: TrendingUp },
    { key: 'nss', label: 'NSS', unit: 'score', icon: Users },
    { key: 'nop', label: 'NOP', unit: 'stands', icon: Building2 },
    { key: 'svs', label: 'SVS', unit: '%', icon: Gauge },
    { key: 'otp', label: 'OTP', unit: '%', icon: Percent },
    { key: 'avg_taxi_time', label: 'Taxi-Out', unit: 'min', icon: PlaneTakeoff, inverse: true },
    { key: 'pax_throughput', label: 'PAX/h', unit: 'pax', icon: Users },
    { key: 'runway_movements', label: 'RWY Mov', unit: 'mov/h', icon: PlaneTakeoff },
];

const AirportDashboardPage: React.FC = () => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';
    const lang = (language as 'es' | 'en') || 'en';
    const tx = TEXTS[lang] || TEXTS.en;
    const horizons = HORIZONS_I18N[lang] || HORIZONS_I18N.en;

    const [horizon, setHorizon] = useState<DemandHorizon>('H+24');
    const [comparison, setComparison] = useState<DashboardComparison | null>(null);
    const [showToBe, setShowToBe] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingProjects, setLoadingProjects] = useState(true);

    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(undefined);
    const [simulations, setSimulations] = useState<Simulation[]>([]);
    const [selectedSimId, setSelectedSimId] = useState<string | undefined>(undefined);
    const [selectedScenario, setSelectedScenario] = useState<string>('s2_ams');

    useEffect(() => { loadProjects(); }, []);

    useEffect(() => {
        if (selectedProjectId) {
            loadSimulations(String(selectedProjectId));
        } else {
            setSimulations([]);
            setSelectedSimId(undefined);
        }
    }, [selectedProjectId]);

    useEffect(() => {
        if (selectedProjectId) {
            loadComparison();
        } else {
            setComparison(null);
            setLoading(false);
        }
    }, [horizon, selectedProjectId, selectedSimId, selectedScenario]);

    const loadProjects = async () => {
        try {
            const projs = await dashboardService.getProjects();
            setProjects(projs);
            if (projs.length > 0) setSelectedProjectId(projs[0].id);
        } finally {
            setLoadingProjects(false);
        }
    };

    const loadSimulations = async (projectId: string) => {
        const sims = await dashboardService.getCompletedSimulations(projectId);
        setSimulations(sims);
        if (sims.length > 0) setSelectedSimId(sims[0].id);
        else setSelectedSimId(undefined);
    };

    const loadComparison = async () => {
        setLoading(true);
        try {
            const airport = projects.find(p => p.id === selectedProjectId)?.airport_icao || 'LEMD';
            const data = await dashboardService.getComparison(horizon, selectedProjectId, selectedSimId, selectedScenario, airport);
            setComparison(data);
        } catch (err) {
            console.error('Error loading dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const getDeltaDisplay = (delta: number, inverse: boolean = false) => {
        const isGood = inverse ? delta < 0 : delta > 0;
        const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
        return { isGood, Icon, text: `${delta > 0 ? '+' : ''}${delta}` };
    };

    const chartData = comparison
        ? comparison.as_is.hourly_data.map((h, i) => ({
            hour: `${h.hour.toString().padStart(2, '0')}:00`,
            'As-Is RAC': h.rac,
            'As-Is Demand': h.demand,
            ...(showToBe && comparison.to_be ? {
                'To-Be RAC': comparison.to_be.hourly_data[i]?.rac || 0,
                'To-Be Demand': comparison.to_be.hourly_data[i]?.demand || 0,
            } : {}),
        }))
        : [];

    // ===================== EMPTY STATE: NO PROJECTS =====================
    if (!loadingProjects && projects.length === 0) {
        const textSub = 'text-[var(--text-secondary)]';
        const textMain = 'text-[var(--text-primary)]';
        return (
            <div className="p-8 font-sans relative min-h-full">
                <div className="max-w-7xl mx-auto">
                    {/* HEADER */}
                    <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b pb-8 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
                        <div>
                            <h1 className={`text-4xl font-extrabold flex items-center gap-3 ${textMain}`}>
                                <LayoutDashboard className="w-8 h-8 text-[#F26430]" />
                                {tx.emptyTitle}
                            </h1>
                            <p className={`text-sm mt-2 tracking-wide font-medium ${textSub}`}>
                                {tx.subtitle}
                            </p>
                        </div>
                    </div>

                    {/* EMPTY STATE */}
                    <div className={`flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface)]/50' : 'border-gray-300 bg-gray-50'}`}>
                        <LayoutDashboard className="h-12 w-12 text-[var(--text-secondary)] mb-4" />
                        <p className={textSub}>
                            {tx.emptyDesc}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container min-h-full" style={{ background: 'var(--bg)' }}>
            <div className="max-w-8xl mx-auto">

                {/* Header */}
                <div className="mb-6 pb-5" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <span className="eyebrow">{tx.eyebrow}</span>
                    <h1 className="text-[1.75rem] mt-1"
                        style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                        {tx.title}
                    </h1>
                    <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                        {tx.subtitle}
                    </p>
                </div>

                {/* Honest preview banner: As-Is is real, To-Be is provisional until the
                    FTS->to-be bridge lands. */}
                <div className="mb-5 p-3 rounded-lg text-[12px] flex items-start gap-2"
                    style={{ background: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-warning) 35%, transparent)', color: 'var(--text-secondary)' }}>
                    <Info size={14} style={{ color: 'var(--aipron-warning)', marginTop: 1, flexShrink: 0 }} />
                    <span>{(tx as any).previewNote}</span>
                </div>

                {/* Selectors Row */}
                <div className="flex flex-wrap gap-4 mb-5 items-end">
                    {/* Project */}
                    <div>
                        <label className="eyebrow block mb-1.5">{tx.projectLabel}</label>
                        <select
                            className="input-field text-[13px] min-w-[200px]"
                            value={selectedProjectId || ''}
                            onChange={e => setSelectedProjectId(e.target.value ? Number(e.target.value) : undefined)}
                        >
                            <option value="" disabled>
                                {tx.selectProject}
                            </option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.name} ({p.airport_icao})</option>
                            ))}
                        </select>
                    </div>

                    {/* Simulation */}
                    <div>
                        <label className="eyebrow block mb-1.5">{tx.simLabel}</label>
                        <select
                            className="input-field text-[13px] min-w-[200px]"
                            value={selectedSimId || ''}
                            onChange={e => setSelectedSimId(e.target.value || undefined)}
                        >
                            <option value="">{tx.demoFallback}</option>
                            {simulations.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Scenario */}
                    <div>
                        <label className="eyebrow block mb-1.5">{tx.scenarioLabel}</label>
                        <select
                            className="input-field text-[13px] min-w-[170px]"
                            value={selectedScenario}
                            onChange={e => setSelectedScenario(e.target.value)}
                        >
                            {(comparison?.available_scenarios || [
                                { id: 's2_ams', name: 'AMS Optimized' },
                                { id: 's3_fastgate', name: 'FASTGate' },
                                { id: 's4_infrastructure', name: 'Infrastructure' },
                            ]).map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* To-Be Toggle */}
                    <button
                        onClick={() => setShowToBe(!showToBe)}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium transition-colors duration-150"
                        style={{
                            background: showToBe ? 'var(--aipron-accent-muted)' : 'var(--surface)',
                            border: `1px solid ${showToBe ? 'var(--aipron-accent)' : 'var(--border)'}`,
                            color: showToBe ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                        }}
                    >
                        {showToBe ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                        To-Be {showToBe ? 'ON' : 'OFF'}
                    </button>
                </div>

                {/* Horizon tabs removed: they did not drive a real per-horizon forecast
                    (cosmetic). The per-demand-horizon comparison returns with the
                    FTS->to-be bridge. */}

                {/* Feature tags */}
                {showToBe && comparison?.to_be && comparison.to_be.enabled_features.length > 0 && (
                    <div className="flex items-center gap-2 mb-5">
                        <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{tx.features}</span>
                        {comparison.to_be.enabled_features.map((f, i) => (
                            <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded"
                                  style={{ fontFamily: 'var(--font-mono)', background: 'var(--aipron-accent-muted)', color: 'var(--aipron-accent)' }}>
                                {f}
                            </span>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-8 h-8 border-2 rounded-full animate-spin mb-4"
                             style={{ borderColor: 'var(--border)', borderTopColor: 'var(--aipron-accent)' }} />
                        <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{tx.loading}</span>
                    </div>
                ) : !comparison ? (
                    <div className={`flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface)]/50' : 'border-gray-300 bg-gray-50'}`}>
                        <LayoutDashboard className="h-12 w-12 text-[var(--text-secondary)] mb-4" />
                        <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                            {lang === 'es' ? 'Sin Datos de Análisis' : 'No Analysis Data'}
                        </h3>
                        <p className="text-sm max-w-md text-center" style={{ color: 'var(--text-secondary)' }}>
                            {lang === 'es'
                                ? 'Ejecuta un análisis DACAMS en el proyecto seleccionado para ver datos en el dashboard.'
                                : 'Run a DACAMS analysis on the selected project to see data in the dashboard.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* KPI Comparison */}
                        <div className="pro-card p-5 mb-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <LayoutDashboard size={16} style={{ color: 'var(--text-tertiary)' }} />
                                    <h2 className="text-[15px] font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                                        {tx.capacityKpis} — {comparison.as_is.airport_code}
                                    </h2>
                                </div>
                                <span className="text-[11px] font-medium" style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                                    {tx.horizon}: {horizon}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                {KPI_CONFIG.map(kpi => {
                                    const asIsVal = comparison.as_is.kpis[kpi.key];
                                    const toBeVal = comparison.to_be?.kpis[kpi.key];
                                    const delta = comparison.deltas[kpi.key] || 0;
                                    const KpiIcon = kpi.icon;
                                    const { isGood, Icon: DeltaIcon, text } = getDeltaDisplay(delta, kpi.inverse);

                                    return (
                                        <div key={kpi.key} className="p-3 rounded-lg"
                                             style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <KpiIcon size={12} style={{ color: 'var(--text-tertiary)' }} />
                                                <span className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                                                      style={{ color: 'var(--text-tertiary)' }}>{kpi.label}</span>
                                            </div>
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-xl font-bold tabular-nums"
                                                      style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                                                    {asIsVal}
                                                </span>
                                                {showToBe && toBeVal !== undefined && (
                                                    <>
                                                        <ChevronRight size={12} style={{ color: 'var(--border)' }} />
                                                        <span className="text-lg font-bold tabular-nums"
                                                              style={{
                                                                  fontFamily: 'var(--font-mono)',
                                                                  color: delta !== 0 ? (isGood ? 'var(--aipron-success)' : 'var(--aipron-danger)') : 'var(--text-tertiary)',
                                                              }}>
                                                            {toBeVal}
                                                        </span>
                                                    </>
                                                )}
                                                <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{kpi.unit}</span>
                                            </div>
                                            {showToBe && delta !== 0 && (
                                                <div className="flex items-center gap-1 mt-1.5 text-[11px] font-semibold tabular-nums"
                                                     style={{ fontFamily: 'var(--font-mono)', color: isGood ? 'var(--aipron-success)' : 'var(--aipron-danger)' }}>
                                                    <DeltaIcon size={11} />
                                                    {text}
                                                    {kpi.inverse && delta < 0 && <span className="font-normal ml-1">{tx.improved}</span>}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="pro-card p-5 mb-5">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendingUp size={16} style={{ color: 'var(--text-tertiary)' }} />
                                <h2 className="text-[15px] font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                                    {tx.hourlyChart}
                                </h2>
                            </div>

                            <ResponsiveContainer width="100%" height={380}>
                                <ComposedChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                                    <XAxis dataKey="hour" stroke="var(--text-tertiary)" tick={{ fontSize: 11 }} />
                                    <YAxis stroke="var(--text-tertiary)" label={{
                                        value: tx.movements, angle: -90, position: 'insideLeft',
                                        style: { fill: 'var(--text-tertiary)', fontSize: 12 },
                                    }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'var(--surface)',
                                            border: '1px solid var(--border)',
                                            borderRadius: '8px',
                                            color: 'var(--text-primary)',
                                            fontFamily: 'var(--font-mono)',
                                            fontSize: '12px',
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="As-Is RAC" fill="#3D5A8080" radius={[2, 2, 0, 0]} />
                                    <Line type="monotone" dataKey="As-Is Demand" stroke="#C33333" strokeWidth={2} dot={false} />
                                    {showToBe && comparison.to_be && (
                                        <>
                                            <Bar dataKey="To-Be RAC" fill="#1D7D5060" radius={[2, 2, 0, 0]} />
                                            <Line type="monotone" dataKey="To-Be Demand" stroke="#F26430" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                                        </>
                                    )}
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Summary */}
                        <div className="pro-card p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <BarChart3 size={16} style={{ color: 'var(--text-tertiary)' }} />
                                <h2 className="text-[15px] font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                                    {tx.summary}
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* As-Is */}
                                <div className="p-4 rounded-lg" style={{ background: 'var(--surface-raised)', borderLeft: '3px solid var(--aipron-info)' }}>
                                    <h3 className="text-[13px] font-bold mb-3" style={{ color: 'var(--aipron-info)', fontFamily: 'var(--font-display)' }}>
                                        {comparison.as_is.scenario_name}
                                    </h3>
                                    <div className="space-y-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                                        <div className="flex justify-between">
                                            <span>{tx.airport}</span>
                                            <span className="font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{comparison.as_is.airport_code}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>RAC</span>
                                            <span className="font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{comparison.as_is.kpis.rac} pos/h</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>CC</span>
                                            <span className="font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{comparison.as_is.kpis.cc} mov/h</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>OTP</span>
                                            <span className="font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{comparison.as_is.kpis.otp}%</span>
                                        </div>
                                    </div>
                                </div>

                                {/* To-Be */}
                                {showToBe && comparison.to_be && (
                                    <div className="p-4 rounded-lg" style={{ background: 'var(--surface-raised)', borderLeft: '3px solid var(--aipron-success)' }}>
                                        <h3 className="text-[13px] font-bold mb-3" style={{ color: 'var(--aipron-success)', fontFamily: 'var(--font-display)' }}>
                                            {comparison.to_be.scenario_name}
                                        </h3>
                                        <div className="space-y-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                                            <div className="flex justify-between">
                                                <span>{tx.airport}</span>
                                                <span className="font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{comparison.to_be.airport_code}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>RAC</span>
                                                <span style={{ fontFamily: 'var(--font-mono)' }}>
                                                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{comparison.to_be.kpis.rac} pos/h</span>
                                                    {(comparison.deltas.rac ?? 0) > 0 && (
                                                        <span className="ml-1.5 font-semibold" style={{ color: 'var(--aipron-success)' }}>+{comparison.deltas.rac}</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>CC</span>
                                                <span style={{ fontFamily: 'var(--font-mono)' }}>
                                                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{comparison.to_be.kpis.cc} mov/h</span>
                                                    {(comparison.deltas.cc ?? 0) > 0 && (
                                                        <span className="ml-1.5 font-semibold" style={{ color: 'var(--aipron-success)' }}>+{comparison.deltas.cc}</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>OTP</span>
                                                <span style={{ fontFamily: 'var(--font-mono)' }}>
                                                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{comparison.to_be.kpis.otp}%</span>
                                                    {(comparison.deltas.otp ?? 0) > 0 && (
                                                        <span className="ml-1.5 font-semibold" style={{ color: 'var(--aipron-success)' }}>+{comparison.deltas.otp}</span>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AirportDashboardPage;
